import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import Loading from '../../components/Loading';
import {
  CreatePaymentSessionInput,
  GetPaymentRouteDeciderQuery,
  PaymentMethod,
  PaymentSessionResult,
  useCreatePaymentSessionMutation,
  useGetPaymentRouteDeciderQuery,
  UserPaymentPlanOption
} from '../../generated/graphql';
import { QuickComplete } from '../Complete/Complete';
import { PaymentCompleteMessage } from '../Complete/TnCSignedFlow';
import PartiallyPaidMessage from './PartiallyPaidMessage';
import PaymentRedirectModal from './PaymentRedirectModal/PaymentRedirectModal';
import PaymentScreen, { PlansByLob } from './PaymentScreen/PaymentScreen';
import UnableToChargeModal from './UnableToChargeModal/UnableToChargeModal';

type RouteDeciderEntry = NonNullable<
  GetPaymentRouteDeciderQuery['paymentRouteDecider']['GL']
>;

// userPaymentPlanOption is only present on InitialSuccess and ChargeSuccess —
// the two states this screen renders. Returns undefined for every other
// variant, which lets the caller assemble a sparse PlansByLob.
const extractPlans = (
  entry: RouteDeciderEntry | null | undefined
): UserPaymentPlanOption[] | undefined => {
  if (
    entry?.__typename === 'InitialSuccess' ||
    entry?.__typename === 'ChargeSuccess'
  ) {
    return entry.userPaymentPlanOptionDeciderResult.userPaymentPlanOption;
  }
  return undefined;
};

// The payment plans common to every product, matched by paymentPlanId. The
// chosen id must exist on all products or createPaymentSession rejects it.
// Single product → returns that product's full list.
const intersectPlans = (plansByLob: PlansByLob): UserPaymentPlanOption[] => {
  const sides = [plansByLob.GL, plansByLob.EO].filter(
    (p): p is UserPaymentPlanOption[] => !!p
  );
  const [first, ...rest] = sides;
  const restIds = rest.map(
    (plans) => new Set(plans.map((p) => p.paymentPlanId))
  );
  return first.filter((p) => restIds.every((ids) => ids.has(p.paymentPlanId)));
};

export interface InitiatePaymentParams {
  invoiceIdOrGroupId: string;
}

const CANADA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon'
];

export const SimpleQuoteExpired: React.FC = () => (
  <div className="m-auto text-lg text-center">Your quote has expired</div>
);

const InitiatePayment: React.FC = () => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.CreditCard
  );
  const [
    paymentPlanOption,
    setPaymentPlanOption
  ] = useState<UserPaymentPlanOption>();
  const [purchaseStartTime, setPurchaseStartTime] = useState<number | null>(
    null
  );
  const [isOpenModal, setOpenModel] = useState(false);
  const [
    disableProceedButtonOnceClick,
    setDisableProceedButtonOnceClick
  ] = useState(false);
  const [useCardForRenewals, setUseCardForRenewals] = useState(true);
  const [country, setCountry] = useState('');
  const { invoiceIdOrGroupId } = useParams<InitiatePaymentParams>();
  const { data, loading, error } = useGetPaymentRouteDeciderQuery({
    variables: { invoiceId: invoiceIdOrGroupId }
  });
  const [
    createPaymentSessionExpired,
    setCreatePaymentSessionExpired
  ] = useState(false);
  const [
    paymentSessionResult,
    setPaymentSessionResult
  ] = useState<PaymentSessionResult>();
  const [createPaymentSession] = useCreatePaymentSessionMutation();

  const fetchPaymentSession = useCallback(async () => {
    if (paymentPlanOption) {
      const createPaymentSessionInput: CreatePaymentSessionInput = {
        invoiceIdOrGroupId,
        paymentPlanId: paymentPlanOption.paymentPlanId,
        method: paymentMethod,
        useCardForRenewals
      };
      const { data: createPaymentData } = await createPaymentSession({
        variables: {
          isContinuing: false,
          paymentInfo: createPaymentSessionInput
        }
      });
      setDisableProceedButtonOnceClick(false);
      if (!createPaymentData) {
        throw new Error('createPaymentData should be ready');
      }
      if (
        ['CancelledRenewalData', 'CompletionData'].includes(
          createPaymentData.createPaymentSession.__typename
        )
      ) {
        setCreatePaymentSessionExpired(true);
      } else {
        setPaymentSessionResult(createPaymentData.createPaymentSession);
      }
    }
  }, [
    paymentPlanOption,
    invoiceIdOrGroupId,
    paymentMethod,
    useCardForRenewals,
    createPaymentSession,
    setPaymentSessionResult
  ]);

  const onClickProceedButtonForChargeSuccess = useCallback(() => {
    setDisableProceedButtonOnceClick(true);
    setOpenModel(true);
    // eslint-disable-next-line no-console
    fetchPaymentSession().catch(console.error);
  }, [fetchPaymentSession]);

  const onClickProceedButtonForNewBusinessAndRenewal = useCallback(() => {
    setDisableProceedButtonOnceClick(true);
    setOpenModel(true);
    // eslint-disable-next-line no-console
    fetchPaymentSession().catch(console.error);
  }, [fetchPaymentSession]);

  useEffect(() => {
    setPurchaseStartTime(new Date().getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Customer-level fields (client address, useCardForRenewals) live on
    // every product's InitialSuccess and are identical across LOBs within a
    // group. Read from whichever product is an InitialSuccess.
    const initial = [
      data?.paymentRouteDecider.GL,
      data?.paymentRouteDecider.EO
    ].find(
      (p): p is Extract<RouteDeciderEntry, { __typename: 'InitialSuccess' }> =>
        p?.__typename === 'InitialSuccess'
    );
    if (initial) {
      const {
        invoiceInfo: { client },
        useCardForRenewals
      } = initial.getInvoiceDeciderResult;
      if (typeof useCardForRenewals === 'boolean') {
        setUseCardForRenewals(useCardForRenewals);
      }

      if (client) {
        const { region: stateName } = client.address;
        const countryName = CANADA_PROVINCES.includes(stateName)
          ? 'Canada'
          : 'United States of America';
        setCountry(countryName);
      }
    }
  }, [data]);

  if (loading) {
    return <Loading />;
  }

  if (createPaymentSessionExpired) {
    return <SimpleQuoteExpired />;
  }

  if (error || !data) {
    if (purchaseStartTime && timeout(purchaseStartTime)) {
      //TODO: delete it
      return <QuickComplete />;
    } else {
      return <Redirect to="/404" />;
    }
  }

  // Either LOB will do as the switch driver: the backend's state-match
  // invariant guarantees GL and EO share the same __typename when both are
  // present, so the routing decision is identical regardless of which one we
  // read.
  const paymentRouteDeciderData =
    data.paymentRouteDecider.GL ?? data.paymentRouteDecider.EO;
  if (!paymentRouteDeciderData) {
    return <PaymentCompleteMessage isNotFound />;
  }

  switch (paymentRouteDeciderData.__typename) {
    case 'FoxdenError': {
      const errorMessage = paymentRouteDeciderData.message;
      if (/isn't authorized/i.test(errorMessage)) {
        return <PaymentCompleteMessage isUnauthorized />;
      } else {
        return <PaymentCompleteMessage isNotFound />;
      }
    }
    case 'IsExpired': {
      return <PaymentCompleteMessage isExpired />;
    }
    case 'CancelledRenewalData': {
      return <SimpleQuoteExpired />;
    }
    case 'PartiallyPaid': {
      const { redirectUrl } = paymentRouteDeciderData;
      return <PartiallyPaidMessage redirectUrl={redirectUrl} />;
    }
    case 'IsPaid': {
      const { policyNumber } = paymentRouteDeciderData;
      return <PaymentCompleteMessage policyFoxdenId={policyNumber} />;
    }
    case 'WaiveSuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceIdOrGroupId}`,
            state: { flowKind: 'WaiveSuccess' }
          }}
        />
      );
    }
    case 'RefundFullPaySuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceIdOrGroupId}`,
            state: { flowKind: 'RefundFullPaySuccess' }
          }}
        />
      );
    }
    case 'RefundInstallmentSuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceIdOrGroupId}`,
            state: { flowKind: 'RefundInstallmentSuccess' }
          }}
        />
      );
    }
    case 'CancellationSuccess': {
      return (
        <Redirect
          to={{
            pathname: `/complete/${invoiceIdOrGroupId}`,
            state: { flowKind: 'CancellationSuccess' }
          }}
        />
      );
    }
    case 'InitialSuccess': {
      if (isOpenModal) {
        if (!paymentPlanOption) {
          throw Error('paymentPlanOption is not selected');
        }
        if (paymentSessionResult?.__typename === 'ActiveRenewalFailureData') {
          return (
            <UnableToChargeModal
              activeRenewalFailureData={paymentSessionResult}
              paymentMethod={paymentMethod}
            />
          );
        }
        return (
          <PaymentRedirectModal
            paymentMethod={paymentMethod}
            paymentSessionResult={paymentSessionResult}
            setClose={() => setOpenModel(false)}
          />
        );
      }
      const plansByLob: PlansByLob = {
        GL: extractPlans(data.paymentRouteDecider.GL),
        EO: extractPlans(data.paymentRouteDecider.EO)
      };
      const planOptions = intersectPlans(plansByLob);
      if (planOptions.length === 0) {
        return <PaymentCompleteMessage isNotFound />;
      }
      return (
        <PaymentScreen
          plansByLob={plansByLob}
          planOptions={planOptions}
          isInitial={true}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          paymentPlanOption={paymentPlanOption}
          setPaymentPlanOption={setPaymentPlanOption}
          useCardForRenewals={useCardForRenewals}
          setUseCardForRenewals={setUseCardForRenewals}
          country={country}
          disableProceedButtonOnceClick={disableProceedButtonOnceClick}
          onProceed={onClickProceedButtonForNewBusinessAndRenewal}
        />
      );
    }
    case 'ChargeSuccess': {
      if (isOpenModal) {
        if (!paymentPlanOption) {
          throw Error('paymentPlanOption is not selected');
        }
        return (
          <PaymentRedirectModal
            paymentMethod={paymentMethod}
            paymentSessionResult={paymentSessionResult}
            setClose={() => setOpenModel(false)}
          />
        );
      }
      const plansByLob: PlansByLob = {
        GL: extractPlans(data.paymentRouteDecider.GL),
        EO: extractPlans(data.paymentRouteDecider.EO)
      };
      const planOptions = intersectPlans(plansByLob);
      return (
        <PaymentScreen
          plansByLob={plansByLob}
          planOptions={planOptions}
          isInitial={false}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          paymentPlanOption={paymentPlanOption}
          setPaymentPlanOption={setPaymentPlanOption}
          useCardForRenewals={useCardForRenewals}
          setUseCardForRenewals={setUseCardForRenewals}
          country={country}
          disableProceedButtonOnceClick={disableProceedButtonOnceClick}
          onProceed={onClickProceedButtonForChargeSuccess}
        />
      );
    }
    default:
      return <PaymentCompleteMessage isNotFound />;
  }
};

const timeout = (startTime: number): boolean => {
  // Timeout of lambda function is 29 seconds.
  const timeLimit = 28.9 * 1000;
  const currentTime = new Date().getTime();
  //TODO delete it
  if (startTime + timeLimit < currentTime) {
    return true;
  }

  return false;
};

export default InitiatePayment;
